import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { StatusBadge, PriorityBadge } from '../components/StatusBadge';
import { formatDate } from '../utils/dateUtils';
import { useSessionState } from '../hooks/useSessionState';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Search, Eye, AlertTriangle, Bell } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EscalatedRequestsPage() {
  const navigate = useNavigate();
  const { getAuthHeader, employee } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useSessionState('escalated:search', '');

  const role = employee?.role;
  const canAccess = ['IT Team', 'IT Head', 'Super Admin', 'Management'].includes(role);

  const fetchEscalated = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/it/escalated`, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setTickets(data);
      }
    } catch (e) {
      console.error('Fetch escalated error:', e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (canAccess) fetchEscalated();
  }, [canAccess, fetchEscalated]);

  useEffect(() => {
    if (!canAccess) return;
    const id = setInterval(fetchEscalated, 30000);
    return () => clearInterval(id);
  }, [canAccess, fetchEscalated]);

  if (!canAccess) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Access denied. IT role required.</p>
        </div>
      </Layout>
    );
  }

  const q = searchQuery.toLowerCase();
  const filtered = tickets.filter(t =>
    t.ticket_id?.toLowerCase().includes(q) ||
    t.request_type?.toLowerCase().includes(q) ||
    t.requester_name?.toLowerCase().includes(q)
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="escalated-requests-page">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-red-50 dark:bg-red-950/40 text-red-600">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Escalated Requests</h1>
            <p className="text-muted-foreground mt-1">IT tickets that breached SLA — needs immediate action</p>
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-red-500" /> {tickets.length} Escalated
              </CardTitle>
              <CardDescription>Auto-escalated after 4 working-hour SLA breach</CardDescription>
            </div>
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search escalated tickets…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="escalated-search"
              />
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No escalated tickets. All clear!
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ticket</TableHead>
                      <TableHead>Request Type</TableHead>
                      <TableHead>Requester</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Currently With</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Escalated At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(t => (
                      <TableRow
                        key={t.ticket_id}
                        data-testid={`escalated-row-${t.ticket_id}`}
                        className="cursor-pointer hover:bg-muted/40"
                        onClick={() => navigate(`/tickets/${t.ticket_id}`)}
                      >
                        <TableCell>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/tickets/${t.ticket_id}`); }}
                            className="font-mono text-xs text-primary hover:underline"
                            data-testid={`open-esc-${t.ticket_id}`}
                          >
                            {t.ticket_id}
                          </button>
                        </TableCell>
                        <TableCell>{t.request_type}</TableCell>
                        <TableCell>{t.requester_name}</TableCell>
                        <TableCell>{t.ticket_location || '-'}</TableCell>
                        <TableCell className="text-xs">{t.assigned_it_name || <span className="text-muted-foreground">Unassigned</span>}</TableCell>
                        <TableCell><PriorityBadge priority={t.priority} /></TableCell>
                        <TableCell className="text-xs">
                          <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="w-3 h-3" />{formatDate(t.escalated_at || t.updated_at)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
